<template>
  <div class="l-page l-page--analysis">
    <!-- サイドバーエリア -->
    <page-sidebar />
    <!-- /サイドバーエリア -->
    <main class="l-main">
      <div class="l-main-head">
        <!-- パンくずリスト -->
        <breadcrumb :breadcrumb-data="breadcrumbData" :additional-classes="['p-breadcrumb--analysis']" />
        <!-- /パンくずリスト -->
        <date-range-picker
          ref="refCalendarModal"
          :initial-start-date="calendarStartDate"
          :initial-end-date="calendarEndDate"
          :on-date-range-changed="onDateRangeChanged"
          cancel-button-label="キャンセル"
          enter-button-label="決定"
        />
      </div>
      <div class="l-main-content">
        <div class="p-analysis-interest">
          <h2 class="c-title">
            <div class="c-title__text u-fs34">興味関心での傾向</div>
            <div class="c-date u-w400">
              <ul class="c-date-list">
                <li class="c-date-list__item c-date-list__item--label">表示期間</li>
                <li class="c-date-list__item c-date-list__item--from">{{ displayStartDate }}</li>
                <li class="c-date-list__item c-date-list__item--to">{{ displayEndDate }}</li>
              </ul>
            </div>
          </h2>
          <analysis-tab
            :current="tab.current"
            :on-select="onTabClick"
          />
          <div
            class="p-analysis-interest__graph p-analysis-interest__graph--treemap"
            :class="[{'is-loading': isLoadingTreemap}, {'is-empty': isEmptyTreemap}]"
          >
            <div class="c-loader"></div>
            <div class="c-empty">
              <div class="c-empty__text">配信後にデータが反映されます</div>
            </div>
            <svg class="c-graph-treemap" xmlns="http://www.w3.org/2000/svg" />
            <div class="c-tooltip c-tooltip--analysis c-tooltip--treemap">
              <div class="c-tooltip__title">-</div>
              <ul class="c-tooltip-list">
                <li class="c-tooltip-list__item c-tooltip-list__item--label">-</li>
                <li class="c-tooltip-list__item c-tooltip-list__item--metrics">-</li>
              </ul>
            </div>
          </div>
          <h3 class="c-title">
            <div class="c-title__text u-fs--l">キーワード</div>
          </h3>
          <div
            class="p-analysis-interest__graph p-analysis-interest__graph--wordcloud"
            :class="[{'is-loading': isLoadingWordCloud}, {'is-empty': isEmptyWordCloud}]"
          >
            <div class="c-loader"></div>
            <div class="c-empty">
              <div class="c-empty__text">配信後にデータが反映されます</div>
            </div>
            <svg class="c-graph-wordcloud" xmlns="http://www.w3.org/2000/svg" />
          </div>
        </div>
      </div>
    </main>
    <!-- モーダル: エラーメッセージ表示 -->
    <error-modal
      :is-active=isError
      :message=errorMessage
      :on-close-click="onCloseErrorModal"
    />
  </div>
</template>

<script>
import axios from "axios";
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');

export default {
  props: {
    breadcrumbData: Array,
    displayPeriod: Object,
  },

  data () {
    return {
      errorMessage: '',
      treemapModule: null,
      wordCloudModule: null,
      isEmptyTreemap: false,
      isEmptyWordCloud: false,
      isError: false,
      isLoadingTreemap: true,
      isLoadingWordCloud: true,
      range: {
        from: this.displayPeriod && this.displayPeriod.from && dayjs(this.displayPeriod.from).isValid()
          ? dayjs(this.displayPeriod.from).toDate()
          : dayjs().add(-30, 'days').toDate(),
        to: this.displayPeriod && this.displayPeriod.to && dayjs(this.displayPeriod.to).isValid()
          ? dayjs(this.displayPeriod.to).toDate()
          : dayjs().toDate(),
      },
      tab: {
        current: 'delivery',
      },
      sort: {
        key: 'delivery',
        type: 'desc'
      }
    }
  },

  computed: {
    calendarStartDate: function() {
      return dayjs(this.range.from).isValid()
        ? dayjs(this.range.from).format('YYYY-MM-DD')
        : dayjs().add(-30, 'days').format('YYYY-MM-DD')
    },
    calendarEndDate: function() {
      return dayjs(this.range.to).isValid()
        ? dayjs(this.range.to).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
    },
    displayStartDate: function() {
      return dayjs(this.range.from).isValid()
        ? dayjs(this.range.from).format('YYYY/MM/DD')
        : '-'
    },
    displayEndDate: function() {
      return dayjs(this.range.to).isValid()
        ? dayjs(this.range.to).format('YYYY/MM/DD')
        : '-'
    }
  },

  mounted: function() {
    // グラフモジュールの読み込み
    import('../../funcs/components/graph/graph-treemap-delivery-interest').then(module => {
      // モジュールオブジェクトをプロパティに設定
      this.treemapModule = module;
      // モジュール読み込み後に初期化処理実行
      this.updateTreemap();
    });

    // グラフモジュールの読み込み
    import('../../funcs/components/graph/graph-wordcloud-delivery-interest').then(module => {
      // モジュールオブジェクトをプロパティに設定
      this.wordCloudModule = module;
      // モジュール読み込み後に初期化処理実行
      this.updateWordCloud();
    });
  },

  methods: {
    updateTreemap: function() {
      // グラフモジュールの読み込みが完了していない場合は処理終了
      if (!this.treemapModule) return;

      // ローダー表示設定
      this.isLoadingTreemap = true;

      // 送信パラメーター作成
      const params = {
        from: dayjs(this.range.from).format('YYYY-MM-DD'),
        to: dayjs(this.range.to).format('YYYY-MM-DD'),
      }

      // ツリーマップ用データの取得処理実行
      axios.get(`/api/analysis/summary-interest/${this.tab.current}`, {
        params
      }).then(res => {
        // レスポンスデータの内容がイレギュラーな場合は処理終了
        if (!res.data || !res.data.items) throw new Error('Invalid response data.');

        // ツリーマップ更新
        this.treemapModule.treemap(res.data);

        // ツリーマップのイベント設定は毎読み込み事に行う必要があるため、毎回実行
        this.treemapModule.setEvent();

        // ローダー非表示設定
        setTimeout(() => {
          this.isLoadingTreemap = false;
        }, 300)
      })
        .catch(_e => {
          // エラーモーダル表示
          this.isError = true;
          this.errorMessage = '興味関心の傾向データの取得に失敗しました';

          // ローダー非表示設定
          setTimeout(() => {
            this.isLoadingTreemap = false;
          }, 300)
        });
    },

    updateWordCloud: function() {
      // グラフモジュールの読み込みが完了していない場合は処理終了
      if (!this.treemapModule) return;

      // ローダー表示設定
      this.isLoadingWordCloud = true;

      // 送信パラメーター作成
      const params = {
        from: dayjs(this.range.from).format('YYYY-MM-DD'),
        to: dayjs(this.range.to).format('YYYY-MM-DD'),
      }

      // ワードクラウド用データの取得処理実行
      axios.get(`/api/analysis/summary-wordcloud/`, {
        params
      }).then(res => {
        // レスポンスデータの内容がイレギュラーな場合は処理終了
        if (!res.data || !res.data.items || !res.data.group) throw new Error('Invalid response data.');

        // ワードクラウド更新
        this.wordCloudModule.wordcloud(res.data);

        // ローダー非表示設定
        setTimeout(() => {
          this.isLoadingWordCloud = false;
        }, 300)
      })
        .catch(_e => {
          // エラーモーダル表示
          this.isError = true;
          this.errorMessage = 'キーワードデータの取得に失敗しました';

          // ローダー非表示設定
          setTimeout(() => {
            this.isLoadingWordCloud = false;
          }, 300)
        });
    },

    onTabClick: function(selected) {
      // 選択値が設定されていない場合は処理終了
      if (!selected) return;

      // 選択値が現在の選択値値と同じ場合は処理終了
      if (selected == this.tab.current) return

      // 選択値を現在のタブ名に設定
      this.tab.current = selected;

      // データ更新
      this.updateTreemap();
      this.updateWordCloud();
    },

    onDateRangeChanged: function(range){
      // 取得データ期間の更新
      this.range.from = range.start;
      this.range.to= range.end;

      // データ更新
      this.updateTreemap();
      this.updateWordCloud();
    },

    // -----------------------------------------------------------------------------------------------------
    // エミット系メソッド一覧: 子孫のUI操作にフックエミットコールされるか、引き渡した関数オブジェクトからコールされるメソッド
    // -----------------------------------------------------------------------------------------------------
    // クリック: エラーモーダルを閉じる処理
    onCloseErrorModal: function() {
      this.isError = false
      this.errorMessage = ''
    },
  }
}
</script>
